/**
 * Manager for creating and configuring Drill meshes
 * Loads drill model from .glb file at runtime with skeletal animations
 */

import {
  Animation,
  type AnimationGroup,
  Color3,
  Mesh,
  MeshBuilder,
  PBRMaterial,
  type Scene,
} from "@babylonjs/core";
import { SceneLoader } from "@babylonjs/core/Loading/sceneLoader.js";
import "@babylonjs/loaders/glTF";
import { BaseManager } from "../core/BaseManager.js";

/** Path to drill.glb file served from public directory */
const DRILL_GLB_PATH = "/3d/original/drill/";
const DRILL_GLB_FILE = "drill.glb";

/**
 * Options for creating Drill mesh
 */
export interface DrillOptions {
  followAppTheme?: boolean;
  scale?: number;
  /** Custom path to GLB directory (default: "/3d/original/drill/") */
  glbPath?: string;
  /** Custom GLB filename (default: "drill.glb") */
  glbFile?: string;
}

/**
 * Result from creating a drill mesh
 */
export interface DrillResult {
  rootMesh: Mesh;
  animationGroups: AnimationGroup[];
}

/**
 * Manager responsible for Drill mesh creation and configuration
 * Loads meshes from .glb file at runtime with skeletal animations
 */
export class DrillManager extends BaseManager {
  private scene: Scene | null = null;
  private instanceCounter = 0;

  /**
   * Initialize the manager (required by BaseManager)
   */
  protected async onInitialize(): Promise<void> {
    // No async initialization needed
  }

  /**
   * Dispose the manager (required by BaseManager)
   */
  protected onDispose(): void {
    this.scene = null;
  }

  /**
   * Set the scene reference for material creation
   */
  setScene(scene: Scene): void {
    this.scene = scene;
  }

  /**
   * Get the current scene
   */
  getScene(): Scene | null {
    return this.scene;
  }

  /**
   * Create Drill mesh by loading from .glb file
   * Returns DrillResult with root mesh and animation groups for control
   */
  async createDrill(
    scene: Scene,
    options: DrillOptions = {},
  ): Promise<DrillResult> {
    const instanceId = this.instanceCounter++;

    // Critical: Enable matrix interpolation for skeletal animations (like Babylon sandbox)
    Animation.AllowMatricesInterpolation = true;

    // Use custom path/file or defaults
    const glbPath = options.glbPath ?? DRILL_GLB_PATH;
    const glbFile = options.glbFile ?? DRILL_GLB_FILE;

    // Load meshes from .glb file
    const result = await SceneLoader.ImportMeshAsync(
      null, // null = load all meshes (glTF convention)
      glbPath,
      glbFile,
      scene,
    );

    // Create a root mesh to hold all parts together (provides geometry for physics)
    const rootMesh = MeshBuilder.CreateBox(
      `drill_root_${instanceId}`,
      { size: 0.001 }, // Tiny invisible box
      scene,
    );
    rootMesh.isVisible = false;

    // Parent ALL loaded meshes to our root (includes __root__ and all children)
    for (const abstractMesh of result.meshes) {
      // Rename for unique instance
      abstractMesh.name = `${abstractMesh.name}_${instanceId}`;

      // Only parent top-level meshes (those without parent or with __root__ parent)
      // This preserves the internal hierarchy while putting everything under our root
      if (
        !abstractMesh.parent ||
        abstractMesh.parent.name.startsWith("__root__")
      ) {
        abstractMesh.parent = rootMesh;
      }

      // Enhance material for actual Mesh instances
      if (abstractMesh instanceof Mesh) {
        this.enhanceMaterial(abstractMesh, scene);
      }

      // Store metadata
      abstractMesh.metadata = {
        ...abstractMesh.metadata,
        drillOptions: options,
        type: "drill-part",
      };
    }

    // Apply scale to root only
    const scale = options.scale ?? 1;
    rootMesh.scaling.setAll(scale);

    // Store metadata on root
    rootMesh.metadata = {
      drillOptions: options,
      type: "drill",
    };

    // Play all animations (drill has Circle.024Action and Circle.025Action)
    for (const animGroup of result.animationGroups) {
      animGroup.play(true); // true = loop
    }

    // Emit creation event
    this.eventBus.emit("drill:created", {
      animationCount: result.animationGroups.length,
      meshCount: result.meshes.length,
    });

    return {
      animationGroups: result.animationGroups,
      rootMesh,
    };
  }

  /**
   * Enhance existing material or create new PBR material
   */
  private enhanceMaterial(mesh: Mesh, scene: Scene): void {
    const existingMaterial = mesh.material;

    if (existingMaterial instanceof PBRMaterial) {
      // Enhance existing PBR material
      existingMaterial.environmentIntensity = 0.4;
      existingMaterial.backFaceCulling = false;
      existingMaterial.twoSidedLighting = true;
      return;
    }

    // Create fallback PBR material if needed
    const material = new PBRMaterial(`drillMaterial_${mesh.name}`, scene);
    material.albedoColor = new Color3(0.4, 0.4, 0.45);
    material.metallic = 0.7;
    material.roughness = 0.35;
    material.emissiveColor = new Color3(0.01, 0.01, 0.02);
    material.environmentIntensity = 0.4;
    material.backFaceCulling = false;
    material.twoSidedLighting = true;
    mesh.material = material;
  }

  /**
   * Apply theme colors to Drill meshes
   * Adjusts material intensity based on dark/light mode
   */
  applyThemeColors(meshes: Mesh[], _theme: string, isDarkMode: boolean): void {
    for (const mesh of meshes) {
      const material = mesh.material as PBRMaterial;
      if (material) {
        if (isDarkMode) {
          material.emissiveColor = new Color3(0.02, 0.02, 0.03);
          material.environmentIntensity = 0.5;
        } else {
          material.emissiveColor = new Color3(0.01, 0.01, 0.02);
          material.environmentIntensity = 0.4;
        }
      }
    }

    this.eventBus.emit("drill:theme-applied", { isDarkMode });
  }
}
